import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationExtras,
  ParamMap,
  QueryParamsHandling,
} from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { List, Map } from 'immutable';
import { includes, noop } from 'lodash';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { v4 } from 'uuid';
import { AppointmentDataDispatcher } from '../appointment-data/appointment-data.dispatcher';
import { Appointment } from '../appointment-data/models/appointment.model';
import { BrowserManagerDataDispatcher } from '../browser-manager-data/browser-manager-data.dispatcher';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { Client } from '../client-data/client.model';
import { IConfirmDialogResult } from '../dialog/components/confirm-dialog/confirm-dialog.interface';
import { DialogService } from '../dialog/dialog.service';
import {
  ADD_APPOINTMENT_QUERY_PARAM,
  ADD_CLIENT_ACTION,
  ADD_CLIENT_QUERY_PARAM,
  ADD_JOB_QUERY_PARAM,
  ADD_TRACKED_TIME_QUERY_PARAM,
  DocumentActionsEnum,
  DocumentErrorsEnum,
  DocumentOverallStatesEnum,
  DOCUMENTS_CREATE_ROUTE,
  DOCUMENTS_NEW_ROUTE,
  DOCUMENTS_PREVIEW_ROUTE,
  DOCUMENTS_SEND_ROUTE,
  DocumentTypesEnum,
  ICopyDocumentOptions,
  UPDATE_DEPOSIT_REQUEST_ACTION,
} from '../document-data/document-data.data';
import { Document } from '../document-data/document.model';
import { DocumentItem } from '../document-data/sub-models/document-item.model';
import { Shipping } from '../document-data/sub-models/shipping.model';
import {
  EstimateFiltersEnum,
  EstimateStatusesEnum,
} from '../estimate-data/estimate-data.data';
import {
  ESTIMATES_DONE_ROUTE,
  ESTIMATES_PENDING_ROUTE,
} from '../estimates/estimates.constant';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { Toggles } from '../features-data/sub-models/toggles.model';
import {
  INVOICES_PAID_ROUTE,
  INVOICES_UNPAID_ROUTE,
} from '../invoices/invoices.data';
import { LocalizationService } from '../localization/localization.service';
import {
  INavigationBackResolveData,
  RESOLVE_KEY as navigationBackResolveKey,
} from '../navigation/navigation.data';
import { NavigationService } from '../navigation/navigation.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { Tax } from '../settings-data/sub-models/company-settings/tax.model';
import { DocumentPresetSettings } from '../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { GlobalHeaderThemesEnum } from '../shared/components/global-header/global-header-themes.enum';
import { IPreventWindowUnloadingOptions } from '../shared/directives/prevent-window-unloading/prevent-window-unloading.directive';
import { IRouter } from '../shared/interfaces/router.interface';
import { LoggerService } from '../shared/services/logger/logger.service';
import { UtilsService } from '../shared/services/utils/utils.service';
import { TrackedTime } from '../tracked-time-data/models/tracked-time.model';
import { TrackedTimeDataDispatcher } from '../tracked-time-data/tracked-time-data.dispatcher';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../tracking/tracking.data';
import { TrackingDispatcher } from '../tracking/tracking.dispatcher';
import { XeroAccount } from '../xero-data/xero-account.model';
import { XeroDataStore } from '../xero-data/xero-data.store';
import { IDocumentAction } from './components/document-actions/document-actions.component';
import { DIALOG_TYPE as depositRequestDialog } from './components/deposit-request-dialog/deposit-request-dialog.component';
// eslint-disable-next-line max-len
import { DIALOG_TYPE as updateDepositRequestDialog } from './components/update-deposit-request-dialog/update-deposit-request-dialog.component';
import { DIALOG_TYPE as missingClientEmailDialog } from './components/missing-client-email-dialog/missing-client-email-dialog.component';
import { DocumentDetailBuilder } from './document-detail.builder';
import { ClientDataDispatcher } from '../client-data/client-data.dispatcher';
import { DepositToggleValue } from '../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import {
  delay,
  distinctUntilChanged,
  filter,
  finalize,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { InvoiceFiltersEnum } from '../invoice-data/invoice-data.data';
import { IDocumentStore } from '../document-data/document-store.interface';
import { IDocumentDataBuilder } from '../document-data/document-data-builder.interface';
import { IDocumentDataDispatcher } from '../document-data/document-data-dispatcher.interface';
import { supportUrlsLocalized, URLS } from '../shared/constants/urls.constant';
import {
  DIALOG_TYPE as pdfRerenderDialog,
  IPdfRendererDialogData,
} from '../renderer/components/pdf-rerender-dialog/pdf-rerender-dialog.component';
import { DIALOG_TYPE as popupBlockedDialog } from '../dialog/components/popup-blocked-dialog/popup-blocked-dialog.component';
import { IError } from '../entity-data/entity-data.data';
import { JobDataDispatcher } from '../job-data/job-data.dispatcher';
import { CLIENTS_MASTER_ROUTE } from '../main/main.data';
import { CLIENTS_JOBS_ROUTE } from '../client-data/client-data.data';
import {
  IFeaturesDataResolveData,
  RESOLVE_KEY as featureDataResolveKey,
} from '../features-data/features-data.data';
import { DocumentDetailService } from './document-detail.service';

@Component({
  selector: 'app-document-detail',
  templateUrl: './document-detail.component.html',
  styleUrls: ['./document-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentDetailBuilder],
})
export class DocumentDetailComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() header: string;
  @Input() masterRoute: string;
  @Input() confirmDialogId: string;
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() documentDataStore: IDocumentStore;
  @Input() documentDataBuilder: IDocumentDataBuilder;
  @Input() preventWindowUnloadingFn: () => boolean;
  @Input() featureName: string;
  @Input() enableShipping: boolean;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() enableDeposits: boolean;
  @Input() enableBankTransfer: boolean;

  @Output()
  onConfirmDialogClose: EventEmitter<IConfirmDialogResult> = new EventEmitter<IConfirmDialogResult>();
  @Output()
  onDocumentAction: EventEmitter<IDocumentAction> = new EventEmitter<IDocumentAction>();

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _documentId: string;

  resendDocumentDialogId: string;
  headerTheme: GlobalHeaderThemesEnum;
  preventWindowUnloadingOptions: IPreventWindowUnloadingOptions;
  tabs: List<IRouter>;
  documentSavedAction: ITrackingAction;
  resendPrimaryActionTapped: ITrackingAction;
  document$: Observable<Document>;
  documentType$: Observable<DocumentTypesEnum>;
  development;
  documentNumber$: Observable<string>;
  documentShipping$: Observable<Shipping>;
  documentSettings$: Observable<DocumentPresetSettings>;
  documentTrackingObject$: Observable<ITrackingObject>;
  taxes$: Observable<List<Tax>>;
  featureToggles$: Observable<Toggles>;
  settings$: Observable<Settings>;
  xeroAccount$: Observable<XeroAccount>;
  goBackRoute$: BehaviorSubject<string>;
  savingDocument$: BehaviorSubject<boolean>;
  jobId: string;
  clientId: string;
  toggles: Toggles;

  constructor(
    private _route: ActivatedRoute,
    private _documentDetailBuilder: DocumentDetailBuilder,
    private _clientDataDispatcher: ClientDataDispatcher,
    private _trackedTimeDataDispatcher: TrackedTimeDataDispatcher,
    private _appointmentDataDispatcher: AppointmentDataDispatcher,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _trackingDispatcher: TrackingDispatcher,
    private _xeroDataStore: XeroDataStore,
    private _featuresDataStore: FeaturesDataStore,
    private _settingsDataStore: SettingsDataStore,
    private _loggerService: LoggerService,
    private _dialogService: DialogService,
    private _globalBannerService: GlobalBannerService,
    private _navigationService: NavigationService,
    private _translateService: TranslateService,
    private _utilsService: UtilsService,
    private _localStorageService: LocalStorageService,
    private _localizationService: LocalizationService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _jobDataDispatcher: JobDataDispatcher,
    private _documentDetailService: DocumentDetailService
  ) {}

  ngOnInit() {
    const routeSnapshot: ActivatedRouteSnapshot = this._route.snapshot;
    const queryParamMap = routeSnapshot.queryParamMap;
    const documentId = routeSnapshot.paramMap.get('id');
    const { goBackUrl } = routeSnapshot.data[
      navigationBackResolveKey
    ] as INavigationBackResolveData;

    const { toggles }: IFeaturesDataResolveData = routeSnapshot.data[
      featureDataResolveKey
    ];

    this.toggles = toggles;
    this._documentId = documentId;

    this.resendDocumentDialogId = v4();
    this.headerTheme = GlobalHeaderThemesEnum.action_packed;
    this.preventWindowUnloadingOptions = {
      onBeforeUnload: this.preventWindowUnloadingFn,
    };
    this.tabs = this._documentDetailBuilder.buildTabs(!documentId);
    this.documentSavedAction = this._trackingActionBuilder.buildSaved(
      !!documentId
    );
    this.resendPrimaryActionTapped = this._trackingActionBuilder.buildButtonTapped(
      'resend_document',
      this.trackingScreenName
    );

    this.goBackRoute$ = new BehaviorSubject(this.masterRoute);
    this.savingDocument$ = new BehaviorSubject(false);

    this.featureToggles$ = this._featuresDataStore.featureToggles$;
    this.xeroAccount$ = this._xeroDataStore.xeroAccount$;

    // set from document
    const validDocument$: Observable<Document> = this.documentDataStore
      .getEditingEntity()
      .pipe(
        filter((x) => !!x),
        distinctUntilChanged((a, b) => a && a.equals(b)),
        shareReplay(1)
      );

    this.document$ = validDocument$;
    this.documentType$ = validDocument$.pipe(
      map((doc: Document) => doc.documentType),
      take(1),
      shareReplay(1)
    );
    this.documentShipping$ = validDocument$.pipe(
      map((doc: Document) => doc.shipping),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
    this.documentNumber$ = validDocument$.pipe(
      map((doc: Document) => doc.docNumber),
      distinctUntilChanged()
    );
    this.documentSettings$ = validDocument$.pipe(
      map((doc: Document) => doc.settings),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
    this.documentTrackingObject$ = validDocument$.pipe(
      map((doc: Document) =>
        this._trackingObjectBuilder.buildDocument(
          doc.id,
          doc.localId,
          doc.docNumber,
          doc.overallState,
          doc.revisionId,
          doc.documentType
        )
      ),
      shareReplay(1)
    );

    // set from settings
    this.settings$ = this._settingsDataStore.settings$.pipe(
      filter((x) => !!x),
      distinctUntilChanged((a, b) => a && a.equals(b)),
      shareReplay(1)
    );
    this.taxes$ = this.settings$.pipe(
      map((settings: Settings) => settings.taxes),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    // set goBackUrl after refresh on document detail page
    validDocument$
      .pipe(
        filter(() => !goBackUrl),
        distinctUntilChanged(
          (a: Document, b: Document) => a && a.states.equals(b.states)
        ),
        withLatestFrom(this.featureToggles$),
        map(([document, toggles]: [Document, Toggles]) => {
          if (this.jobId && this.clientId) {
            const jobRoute = this._getJobRoute(this.jobId, this.clientId);
            return jobRoute.join('/');
          }

          if (document.documentType === DocumentTypesEnum.invoice) {
            const invoiceFilter: InvoiceFiltersEnum =
              this._utilsService.calculateDocState(document.states, toggles) ===
              DocumentOverallStatesEnum.fully_paid
                ? InvoiceFiltersEnum.paid
                : InvoiceFiltersEnum.unpaid;

            return Location.joinWithSlash(this.masterRoute, invoiceFilter);
          }

          if (document.documentType === DocumentTypesEnum.estimate) {
            const estimateFilter: EstimateFiltersEnum =
              document.states.estimateStatus === EstimateStatusesEnum.pending
                ? EstimateFiltersEnum.pending
                : EstimateFiltersEnum.done;

            return Location.joinWithSlash(this.masterRoute, estimateFilter);
          }

          return this.masterRoute;
        }),
        takeUntil(this._destroyed)
      )
      .subscribe((route) => this.goBackRoute$.next(route));

    this.documentDataDispatcher.resetEditingEntity();

    this.initDocumentDetail(queryParamMap, documentId);
  }

  ngAfterViewInit() {
    if (this.trackingScreenName) {
      const previouslyExisted = !!this._route.snapshot.paramMap.get('id');
      const openedAction = this._trackingActionBuilder.buildOpened(
        previouslyExisted,
        this.trackingScreenName
      );

      this.documentTrackingObject$
        .pipe(
          filter((x) => !!x),
          take(1)
        )
        .subscribe((trackingObject) =>
          this._trackingDispatcher.trackEvent(trackingObject, openedAction)
        );
    }
  }

  initDocumentDetail(queryParamMap: ParamMap, documentId: string) {
    const seedDataId = queryParamMap.get('seedDataId');
    const trackedTimeId = queryParamMap.get(ADD_TRACKED_TIME_QUERY_PARAM);
    const appointmentId = queryParamMap.get(ADD_APPOINTMENT_QUERY_PARAM);
    const existingId = documentId || seedDataId;

    this.jobId = queryParamMap.get(ADD_JOB_QUERY_PARAM);
    this.clientId = queryParamMap.get(ADD_CLIENT_QUERY_PARAM);

    const document$ = existingId
      ? this.getExistingDocument(existingId)
      : this.getNewDocument();
    const client$ = this.clientId
      ? this._clientDataDispatcher.getEntityById(this.clientId)
      : of(null);
    const trackedTime$ = trackedTimeId
      ? this._trackedTimeDataDispatcher.getEntityById(trackedTimeId)
      : of(null);
    const appointment$ = appointmentId
      ? this._appointmentDataDispatcher.getEntityById(appointmentId)
      : of(null);
    const settings$ = this._settingsDataStore.getSettings();
    const toggles$ = this._featuresDataStore.getFeatureToggles();

    if (seedDataId) {
      this._dialogService.closeLoadingDialog();
    }

    forkJoin(
      document$,
      client$,
      trackedTime$,
      appointment$,
      settings$,
      toggles$
    )
      .pipe(
        flatMap(
          ([
            document,
            client,
            trackedTime,
            appointment,
            settings,
            featureToggles,
          ]: [
            Document,
            Client,
            TrackedTime,
            Appointment,
            Settings,
            Toggles
          ]) => {
            const isDirty = document.isDirty();
            const isNew = document.isNew();

            if (client && !client.isDeleted()) {
              document = this._documentDetailBuilder.buildDocumentWithClient(
                document,
                client
              );
            }

            if (trackedTime) {
              const documentItemProps = this._documentDetailBuilder.buildDocumentItemPropsFromTrackedTime(
                trackedTime,
                document.settings
              );
              const documentItem: DocumentItem = this.documentDataBuilder.buildDocumentItem(
                documentItemProps,
                document.taxes,
                document.withholdingTax
              );

              document = this._documentDetailBuilder.buildDocumentWithDocumentItem(
                document,
                documentItem
              );
            }

            if (appointment) {
              const documentItemProps = this._documentDetailBuilder.buildDocumentItemPropsFromAppointment(
                appointment,
                document.settings
              );
              const documentItem: DocumentItem = this.documentDataBuilder.buildDocumentItem(
                documentItemProps,
                document.taxes,
                document.withholdingTax
              );

              document = this._documentDetailBuilder.buildDocumentWithDocumentItem(
                document,
                documentItem
              );
            }

            // auto-create a new deposit based on settings
            if (!document.deposit && isNew) {
              const documentType = document.documentType;
              const defaultDepositToggle: DepositToggleValue =
                settings.companySettings.depositToggle[documentType];
              const depositRequestFeatureName =
                documentType === DocumentTypesEnum.invoice
                  ? Toggles.KEYS.DEPOSITS_ON_INVOICE
                  : Toggles.KEYS.DEPOSITS_ON_ESTIMATE;

              if (
                defaultDepositToggle &&
                defaultDepositToggle.toggleState &&
                featureToggles.isAllAccess(depositRequestFeatureName) &&
                featureToggles.isAllAccess(Toggles.KEYS.DEPOSIT_TOGGLE)
              ) {
                const dueDate = this._documentDetailBuilder.buildDueDate(
                  document.docDate,
                  document.terms
                );
                const defaultDeposit = this._documentDetailBuilder.buildDefaultDeposit(
                  defaultDepositToggle.value,
                  document.calculationResult,
                  dueDate
                );

                document = this._documentDetailBuilder.buildDocumentWithDeposit(
                  document,
                  defaultDeposit
                );
              }
            }

            return this.saveNewDocument(
              this.documentDataBuilder.buildEntity(
                document,
                null,
                isDirty,
                isNew,
                true
              )
            );
          }
        ),
        // using a flatMap so getTranslations happens synchronously and before the subscribe()
        flatMap((document: Document) => {
          const docLocale: string = document.settings.locale;
          const isLoadedLocale: boolean = includes(
            this._localizationService.getLoadedLangs(),
            docLocale
          );

          return isLoadedLocale
            ? of(document)
            : // load document locale so currency is showed the same in UI and document preview
              this._localizationService
                .getTranslations(docLocale)
                .pipe(map(() => document));
        }),
        takeUntil(this._destroyed)
      )
      .subscribe(
        (document: Document) => {
          this.documentDataDispatcher.setEditingEntity(document);
          this.documentDataDispatcher.saveEntityToStore(
            document,
            document.isNew()
          );
        },
        (iError: IError) => {
          const { error, data } = iError;
          const [status, errorBody] = data;

          if (status >= 400 && status < 500) {
            this._navigationService.goBack(this.goBackRoute$.value);
            return;
          }

          if (error instanceof Error) {
            this._globalBannerService.showSystemErrorBanner(error);
            return;
          }

          this._globalBannerService.showSystemErrorBanner(
            new Error(
              `Failed to get document. error: ${error}. status code: ${status}. response: ${errorBody}`
            )
          );
        }
      );
  }

  documentAction(documentAction: IDocumentAction) {
    const { action, payload } = documentAction;

    switch (action) {
      case DocumentActionsEnum.save_document:
        this.saveDocument();
        break;
      case DocumentActionsEnum.convert_document_to_invoice:
      // fall through
      case DocumentActionsEnum.copy_document:
        this.copyDocument(payload as ICopyDocumentOptions);
        break;
      case DocumentActionsEnum.print_document:
        this.printDocument(payload as Document);
        break;
    }

    this.onDocumentAction.emit(documentAction);
  }

  getNewDocument(): Observable<Document> {
    return this._settingsDataStore.getSettings().pipe(
      filter((x) => !!x),
      map((settings: Settings) =>
        this.documentDataBuilder.buildEntity({}, settings, false, true)
      )
    );
  }

  getExistingDocument(id: string): Observable<Document> {
    return this.documentDataStore.getInTmpCache<Document>(id).pipe(
      take(1),
      flatMap((document: Document) => {
        if (!document) {
          return this.documentDataDispatcher.getEntityById(id);
        }

        this.documentDataDispatcher.deleteFromTmpCache(id);

        return of(document);
      })
    );
  }

  saveNewDocument(document: Document): Observable<Document> {
    return (
      (document.id && of(document)) ||
      this.documentDataDispatcher.saveNewEntity(document)
    );
  }

  updateDocumentContent(documentContentProps: Map<string, any>) {
    this.documentDataDispatcher.setEditingEntityContent(documentContentProps);
  }

  saveDocument() {
    this.documentDataStore
      .getEditingEntity()
      .pipe(
        withLatestFrom(this.featureToggles$, this.settings$),
        take(1),
        takeUntil(this._destroyed)
      )
      .subscribe(
        ([document, toggles, settings]: [Document, Toggles, Settings]) => {
          if (
            this.enableDeposits &&
            document.depositAmountLargerThanOutstandingBalance()
          ) {
            this._dialogService.openDialog({
              type: updateDepositRequestDialog,
            });
            return;
          }

          // if bank transfer is enabled, an invoice needs to have a client with an email address
          if (
            this.enableBankTransfer &&
            toggles.isAllAccess(Toggles.KEYS.STRIPE_ACH_DEBIT) &&
            settings.payments.bankTransferEnabled() &&
            !document.settings.bankTransferDisabled &&
            !document.billing.email
          ) {
            this._dialogService.openDialog({ type: missingClientEmailDialog });
            return;
          }

          if (
            this.documentDataStore.hasEditingEntityChanged() &&
            !!document.states.dateSent
          ) {
            this._dialogService.openConfirmDialog(this.resendDocumentDialogId);
            return;
          }

          // setTimeout in order to save all required fields locally before sending the data
          setTimeout(() => this.saveDocumentToNetwork(), 100);
        }
      );
  }

  onUpdateDepositRequest() {
    const queryParams = { action: UPDATE_DEPOSIT_REQUEST_ACTION };
    const navigationExtras: NavigationExtras = {
      replaceUrl: true,
      relativeTo: this._route,
      queryParams,
    };

    if (
      this._navigationService.isRouteActive([DOCUMENTS_PREVIEW_ROUTE], {
        relativeTo: this._route,
      }) ||
      this._navigationService.isRouteActive([DOCUMENTS_SEND_ROUTE], {
        relativeTo: this._route,
      })
    ) {
      this._dialogService.openDialog({ type: depositRequestDialog });

      return;
    }

    setTimeout(() =>
      this._navigationService.goTo(DOCUMENTS_PREVIEW_ROUTE, navigationExtras)
    );
  }

  onCancelDepositRequest() {
    this.documentDataDispatcher.removeDepositInEditing(false);

    this.saveDocumentToNetwork();
  }

  saveDocumentToNetwork(navigateToList: boolean = true) {
    this.documentDataStore
      .getEditingEntity()
      .pipe(
        take(1),
        filter((document: Document) => document.referencesPopulated()),
        tap(() => {
          this.savingDocument$.next(true);

          this._dialogService.openLoadingDialog(
            this._translateService.instant('LABEL.SAVING')
          );
        }),
        flatMap(() => this.documentDataDispatcher.saveEditingEntity()),
        tap(() => this._dialogService.closeLoadingDialog()),
        filter(() => navigateToList),
        withLatestFrom(this.featureToggles$),
        takeUntil(this._destroyed)
      )
      .subscribe(
        ([document, toggles]: [Document, Toggles]) => {
          if (this.jobId && this.clientId) {
            if (document.documentType === DocumentTypesEnum.estimate) {
              this._jobDataDispatcher.addEstimate(document.id, this.jobId);
            }

            const jobRoute = this._getJobRoute(this.jobId, this.clientId);
            return this._navigationService.goTo(jobRoute);
          }

          this._documentDetailService.triggerJobDialog(this.toggles, document);

          return this.navigateToList(document, toggles);
        },
        (error) => {
          this.savingDocument$.next(false);

          this._dialogService.closeLoadingDialog();

          const supportUrl = supportUrlsLocalized(
            URLS.SUPPORT,
            this._localizationService.getLocale()
          );
          const errorHtml = this._translateService.instant(
            'ERROR.DOCUMENT_NOT_SAVED',
            {
              url: supportUrl,
            }
          );

          this._globalBannerService.showSystemErrorBanner(error, errorHtml);
        }
      );
  }

  navigateToList(document: Document, toggles: Toggles) {
    const route = [this.masterRoute];

    if (document.documentType === DocumentTypesEnum.invoice) {
      const filterRoute: string =
        this._utilsService.calculateDocState(document.states, toggles) ===
        DocumentOverallStatesEnum.fully_paid
          ? INVOICES_PAID_ROUTE
          : INVOICES_UNPAID_ROUTE;

      route.push(filterRoute);
    } else if (document.documentType === DocumentTypesEnum.estimate) {
      const filterRoute: string =
        document.states.estimateStatus === EstimateStatusesEnum.pending
          ? ESTIMATES_PENDING_ROUTE
          : ESTIMATES_DONE_ROUTE;

      route.push(filterRoute);
    }

    this._navigationService.goTo(route);
  }

  onResendDialogClose(dialogResult: IConfirmDialogResult) {
    this.onConfirmDialogClose.emit(dialogResult);

    if (dialogResult.result) {
      const navigationExtras: NavigationExtras = {
        replaceUrl: true,
        relativeTo: this._route,
      };

      this._navigationService.goTo(DOCUMENTS_SEND_ROUTE, navigationExtras);
    } else {
      this.saveDocumentToNetwork();
    }
  }

  copyDocument(copyDocumentOptions: ICopyDocumentOptions) {
    this._dialogService.openLoadingDialog();

    // set timeout so copy dialog closes and loading dialog shows up
    setTimeout(() => {
      this.documentDataDispatcher
        .copyDocument(copyDocumentOptions)
        .pipe(takeUntil(this._destroyed))
        .subscribe(
          noop, // on success, loading dialog is handled by onDestroy the main component
          (error) => {
            this._dialogService.closeLoadingDialog();
            this._globalBannerService.showSystemErrorBanner(error);
          }
        );
    });
  }

  printDocument(document: Document) {
    this._dialogService.openLoadingDialog(
      this._translateService.instant('LABEL.PRINTING')
    );

    // save editing document before sending so server has the latest document
    this.documentDataDispatcher
      .saveEditingEntity()
      .pipe(
        flatMap((savedDocument: Document) =>
          this.documentDataDispatcher.printDocument(savedDocument, true)
        ),
        takeUntil(this._destroyed)
      )
      .subscribe(
        () => this._dialogService.closeLoadingDialog(),
        (err: IError) => {
          const { error } = err;
          const errorMsg = (error && error.message) || '';

          this._dialogService.closeLoadingDialog();

          switch (errorMsg) {
            case DocumentErrorsEnum.document_not_found:
              this._dialogService.openDialog({
                type: pdfRerenderDialog,
                data: {
                  documentType: document.documentType,
                  documentNumber: document.docNumber,
                  document,
                } as IPdfRendererDialogData,
              });
              break;
            case DocumentErrorsEnum.print_popup_blocked:
              this._dialogService.openDialog({ type: popupBlockedDialog });
              break;
            default:
              this._globalBannerService.showSystemErrorBanner(error);
          }
        }
      );
  }

  removeSignatures() {
    this._dialogService.openLoadingDialog();

    this.documentDataDispatcher
      .setEditingEntityContent(
        this._documentDetailBuilder.buildDocumentContentWithoutSignatures()
      )
      .pipe(
        flatMap(() => this.documentDataDispatcher.saveEditingEntity()), // save document so that server has new pdf
        delay(100),
        finalize(() => this._dialogService.closeLoadingDialog()),
        takeUntil(this._destroyed)
      )
      .subscribe(noop, noop);
  }

  onAddClientEmail(documentType: DocumentTypesEnum) {
    const navExtras = {
      queryParams: {
        action: ADD_CLIENT_ACTION,
      },
      skipLocationChange: true,
      queryParamsHandling: 'merge' as QueryParamsHandling,
    };

    return this._navigationService.goTo(
      [
        this._utilsService.getDocumentMasterRoute(documentType),
        this._documentId || DOCUMENTS_NEW_ROUTE,
        DOCUMENTS_CREATE_ROUTE,
      ],
      navExtras
    );
  }

  onDisableBankTransfer() {
    const props = this._documentDetailBuilder.buildDocumentSettingsPropsForBankTransfer(
      false
    );

    this.updateDocumentContent(props);

    this.saveDocument();
  }

  ngOnDestroy() {
    this._dialogService.closeLoadingDialog();
    this._dialogService.closeDialog({ type: popupBlockedDialog });
    this._dialogService.closeDialog({ type: pdfRerenderDialog });
    this._dialogService.closeDialog({ type: updateDepositRequestDialog });
    this._dialogService.closeDialog({ type: missingClientEmailDialog });
    this._dialogService.closeConfirmDialog(this.confirmDialogId);
    this._dialogService.closeConfirmDialog(this.resendDocumentDialogId);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  private _getJobRoute(jobId: string, clientId: string): string[] {
    return [CLIENTS_MASTER_ROUTE, clientId, CLIENTS_JOBS_ROUTE, jobId];
  }
}
